import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 0,
  },
  colors: {
    primary: {
      main: "#000000",
      dark: "#333333",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FFFFFF",
      dark: "#999999",
      contrastText: "#000000",
    },
    error: {
      main: "#BB0A30",
    },
    info: {
      main: "#0082D6",
      light: "#D6EBF9",
    },
    text: {
      primary: "#333333",
      secondary: "#999999",
      hint: "#999999",
    },
    background: "#FFFFFF",
    border: "#999999",
    hover: "#EEEEEE",
    disabled: "#EEEEEE",
    placeholder: "#999999",
    transparent: "#00000000",
    divider: "#EEEEEE",
    dividerAlt: "#CCCCCC",
  },
};

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1",
  fill: "none",
  fillRule: "evenodd",
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);
const fontBody = "Audi Type Screen";
const fontHeading = "AudiTypeExtended";

// More properties here: https://material-ui.com/customization/default-theme/
export const audi = {
  themeName: "Audi",
  brand: BrandsEnum.Audi,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.divider,
      paper: variables.colors.divider,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.background,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.dividerAlt,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily(fontBody),
    color: variables.colors.text.primary,
    h1: {
      fontFamily: getFontFamily(fontHeading),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      fontWeight: 400,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
    },
    h2: {
      fontFamily: getFontFamily(fontHeading),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.primary,
      fontWeight: 900,
      textAlign: "center" as "center",
      paddingBottom: "2rem",
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
    },
    h3: {
      fontFamily: getFontFamily(fontHeading),
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.main,
      fontWeight: 900,
      textDecoration: "underline",
    },
    h4: {
      fontFamily: getFontFamily(fontHeading),
      color: variables.colors.text.primary,
      fontWeight: 900,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontFamily: getFontFamily(fontHeading),
      fontSize: "1.25rem",
      color: variables.colors.primary.main,
      fontWeight: 900,
    },
    h6: {
      fontFamily: getFontFamily(fontHeading),
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: "normal" as "normal",
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
      fontWeight: 900,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    hoverBackgroundColor: variables.colors.hover,
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
    },
    cardSelected: {
      border: `1px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
      },
      contained: {
        border: `1px solid ${variables.colors.primary.main}`,
        boxShadow: props.buttons.boxShadow,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
          borderColor: variables.colors.primary.dark,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
          borderColor: variables.colors.primary.dark,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
          borderColor: variables.colors.primary.dark,
        },
        "&$disabled": {
          backgroundColor: variables.colors.disabled,
          color: variables.colors.border,
          borderColor: variables.colors.border,
        },
        fontWeight: 700,
        "a&": {
          fontWeight: 700,
          border: `1px solid ${variables.colors.primary.main}`,
          "&:hover": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
          textDecoration: "none",
        },
      },
      containedPrimary: {
        fontWeight: 700,
      },
      outlinedSecondary: {
        "&:hover": {
          borderColor: variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        "&:active": {
          borderColor: variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        color: variables.colors.text.primary,
        border: "1px solid " + variables.colors.text.primary,
        "&$disabled": {
          backgroundColor: variables.colors.disabled,
          color: variables.colors.border,
          borderColor: variables.colors.border,
        },
        "a&": {
          "&:hover": {
            borderColor: variables.colors.primary.dark,
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            borderColor: variables.colors.primary.dark,
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.text.primary,
          border: "1px solid " + variables.colors.text.primary,
          textDecoration: "none",
          "& span": {
            textDecoration: "none",
          },
          "&$disabled": {
            backgroundColor: variables.colors.disabled,
            color: variables.colors.border,
            borderColor: variables.colors.border,
          },
        },
      },
      text: {
        color: variables.colors.primary.main,
        textDecoration: "none",
        "&:hover": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
        "&:active": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
      // startIcon: buttonIcons,
      // endIcon: buttonIcons,
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiFormLabel: {
      root: {
        "&$focused": {
          color: variables.colors.border,
        },
      },
    },
    MuiInputLabel: {
      formControl: {
        position: "relative" as PositionProperty,
        transform: "none",
        color: variables.colors.text.primary,
        lineHeight: 1.75, // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        backgroundColor: variables.colors.transparent,
        "&:hover": {
          //backgroundColor: variables.colors.input.hover,
          "& $notchedOutline": {
            borderColor: variables.colors.transparent,
            borderWidth: 1,
            borderBottomColor: variables.colors.primary.main,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.transparent,
            borderWidth: 1,
            borderBottomWidth: 2,
            borderBottomColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.transparent,
            borderWidth: 1,
            borderBottomColor: variables.colors.error.main,
          },
        },
        "&$error $notchedOutline": {
          borderColor: variables.colors.transparent,
          borderWidth: 1,
          borderBottomColor: variables.colors.error.main,
          "&$focused": {
            borderBottomWidth: 2,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.transparent,
          borderWidth: 1,
          borderBottomWidth: 2,
          borderBottomColor: variables.colors.primary.main,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.transparent,
        borderWidth: 1,
        borderBottomColor: variables.colors.border,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          color: variables.colors.text.hint,
          borderLeft: "none",
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiSelect: {
      select: {
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        //...Helpers.svgSelectorWrapper(svgIcons.arrowDown, { svgRelation: '~' }),
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
      },
      icon: {
        fill: variables.colors.primary.main,
        color: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          color: variables.colors.primary.main,
          textDecoration: "underline",
          "&:hover": {
            color: variables.colors.primary.dark,
          },
          "&:active": {
            color: variables.colors.primary.dark,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.primary.main,
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: variables.common.borderRadius,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.secondary.dark,
        border: "1px solid " + variables.colors.secondary.dark,
        backgroundColor: variables.colors.primary.contrastText,
        "&$selected": {
          color: variables.colors.primary.main,
          borderColor: variables.colors.primary.main,
          backgroundColor: variables.colors.primary.contrastText,
          "&:hover": {
            //backgroundColor: variables.colors.hover,
          },
        },
        "&:hover": {
          //backgroundColor: variables.colors.hover,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
    MuiPaper: {
      elevation8: {
        boxShadow: "none",
        border: `1px solid ${variables.colors.primary.main}`,
      },
      root: {
        //...Helpers.svgSelectorWrapper(svgIcons.cancel),
      },
    },
  },
  coverageSelect: {
    root: {
      borderColor: variables.colors.dividerAlt,
      backgroundColor: variables.colors.background,
    },
    selected: {
      borderColor: variables.colors.primary.main,
      backgroundColor: variables.colors.hover,
    },
    descriptionCard: {
      backgroundColor: variables.colors.hover,
    },
    recommended: {
      backgroundColor: variables.colors.primary.main,
      borderColor: variables.colors.primary.main,
      textColor: variables.colors.primary.contrastText,
    },
  },
  footerBar: {
    backgroundColor: variables.colors.primary.dark,
    textColor: variables.colors.primary.contrastText,
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.main,
    },
  },
  popup: {
    root: {
      fontFamily: getFontFamily(fontBody),
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#000000",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily(fontBody),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  icons: {
    infoIcon: (
      <path
        {...defaultPathParams}
        d='M11.5,17 L11.5,10 M11.5,8 L11.5,6 M11.5,22.5 C17.5751322,22.5 22.5,17.5751322 22.5,11.5 C22.5,5.42486775 17.5751322,0.5 11.5,0.5 C5.42486775,0.5 0.5,5.42486775 0.5,11.5 C0.5,17.5751322 5.42486775,22.5 11.5,22.5 Z'
      />
    ),

    helpIcon: (
      <path
        {...defaultPathParams}
        d='M11.5,15 L11.5,16 M9.9,7.5 C9.9,7.5 14,5.9 14.5,8.5 C14.9,10.4 11.5,12.7 11.5,12.7 L11.5,14 M11.5,22.5 C17.5751322,22.5 22.5,17.5751322 22.5,11.5 C22.5,5.42486775 17.5751322,0.5 11.5,0.5 C5.42486775,0.5 0.5,5.42486775 0.5,11.5 C0.5,17.5751322 5.42486775,22.5 11.5,22.5 Z'
      />
    ),

    selectIcon: <path {...defaultPathParams} d='M7 10L12.5 15.5 18 10' />,

    arrowUpIcon: <path {...defaultPathParams} d='M18 15.5L12.5 10 7 15.5' />,

    dateTimeIcon: <path {...defaultPathParams} d='M7 10L12.5 15.5 18 10' />,

    addIcon: (
      <path
        {...defaultPathParams}
        d='M11.5,7 L11.5,16 M16,11.4 L7,11.4 M11.5,22.5 C17.5751322,22.5 22.5,17.5751322 22.5,11.5 C22.5,5.42486775 17.5751322,0.5 11.5,0.5 C5.42486775,0.5 0.5,5.42486775 0.5,11.5 C0.5,17.5751322 5.42486775,22.5 11.5,22.5 Z'
      />
    ),

    removeIcon: (
      <path
        {...defaultPathParams}
        d='M15,11.4 L8,11.4 M11.5,22.5 C17.5751322,22.5 22.5,17.5751322 22.5,11.5 C22.5,5.42486775 17.5751322,0.5 11.5,0.5 C5.42486775,0.5 0.5,5.42486775 0.5,11.5 C0.5,17.5751322 5.42486775,22.5 11.5,22.5 Z'
      />
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M4 12.871L9.54839 18.2903L20 5" stroke="black" />
  </svg>`),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
          fill='#1A1A1A'
        />
      </svg>
    ),

    crossIcon: <path {...defaultPathParams} d='M5.9,17.1 L17.1,5.9 M5.9,5.9 L17.1,17.1' />,

    checkmarkIcon: <path {...defaultPathParams} d='M6 12.2L10.3 16.4 18.4 6.1' />,

    checkmarkSelectedOptionIcon: (
      <>
        <path fill='#000000' stroke='#000000' d='M-1.2071-.5H48.5v49.7071z' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <polygon id='Path' points='6 11.17 1.83 7 0.41 8.41 6 14 18 2 16.59 0.59' transform='translate(12 3) scale(0.5)' fill='#FFF' />
      </>
    ),

    backIcon: (
      <path
        {...defaultPathParams}
        style={{ transform: "rotate(90deg) translateY(-100%)" }}
        d='M16,18 L11.5,22.5 L7,18 M11.5,22.5 L11.5,0'
      />
    ),
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },

  priceText: {
    totalPriceColor: variables.colors.primary.main,
    fontWeight: 700,
  },
  messageBox: {
    backgroundColor: "#E3E9F5",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(audi);
